import * as R from 'ramda';
import { useSelector } from 'react-redux';
import React, { useRef, useState, useEffect, useCallback } from 'react';
// components
import { OutsideReportExactFilterList } from '../components/edit-report';
// features
import { makeSelectAllAvailableReferenceTypesByScopeName } from '../features/reference/selectors';
// helpers/constants
import * as G from '../helpers';
import * as GC from '../constants';
//////////////////////////////////////////////////

const reportTypesMap = {
  [GC.ROUTE_PATH_ITEMS_LIST]: GC.ITEM_REPORT,
  [GC.ROUTE_PATH_CLO_ITEM_LIST]: GC.CLO_ITEM_REPORT,
  [GC.ROUTE_PATH_CONTACT_BOOK]: GC.CONTACT_BOOK_REPORT,
  [GC.ROUTE_PATH_FLEET_TRUCKS_LIST]: GC.FLEET_TRUCK_REPORT,
  [GC.ROUTE_PATH_TEMPLATES_DO_LIST]: GC.CLO_TEMPLATE_REPORT,
  [GC.ROUTE_PATH_FLEET_DRIVERS_LIST]: GC.FLEET_DRIVER_REPORT,
  [GC.ROUTE_PATH_LOCATIONS_LIST]: GC.LOCATION_TEMPLATE_REPORT,
  [GC.ROUTE_PATH_FLEET_TRAILERS_LIST]: GC.FLEET_TRAILER_REPORT,
  [GC.ROUTE_PATH_TEMPLATES_ROUTE_LIST]: GC.ROUTE_TEMPLATE_REPORT,
  [GC.ROUTE_PATH_SHARED_ACCESSORIALS]: GC.SHARED_ACCESSORIAL_REPORT,
  [GC.ROUTE_PATH_TEMPLATE_CONTAINER_LIST]: GC.CONTAINER_TEMPLATE_REPORT,
  [GC.ROUTE_PATH_FLEET_SERVICE_ISSUES_LIST]: GC.FLEET_SERVICE_ISSUE_REPORT,
  [GC.ROUTE_PATH_FLEET_DRIVER_EXPENSES_LIST]: GC.FLEET_DRIVER_EXPENSE_REPORT,
  [GC.ROUTE_PATH_TEMPLATES_COMPENSATION_LIST]: GC.COMPENSATION_TEMPLATE_REPORT,
  [GC.ROUTE_PATH_FLEET_SHARED_COMPONENT_LIST]: GC.FLEET_SHARED_COMPONENT_REPORT,
  [GC.ROUTE_PATH_FLEET_EQUIPMENT_SERVICE_LIST]: GC.FLEET_EQUIPMENT_SERVICE_REPORT,
  [GC.ROUTE_PATH_TEMPLATES_EQUIPMENT_SERVICE_LIST]: GC.EQUIPMENT_SERVICE_TEMPLATE_REPORT,
};

const usePromptModal = (props, settings = []) => {
  const {
    openModal,
    closeModal,
    reportType,
    pageVisited,
    filterProps,
    selectedReport,
    handleSetUsedReport,
    handleChangeReportParams,
  } = props;

  const [openPromptModal, setOpenPromptModal] = useState(false);

  const propsRef = useRef(props);
  const prevSelectedReportGuid = useRef(null);

  const allAvailableRefTypesByScopeName = useSelector(makeSelectAllAvailableReferenceTypesByScopeName());

  useEffect(() => {
    propsRef.current = props;
  }, [props]);

  const handleOpenPromptModal = useCallback(() => {
    const reportTypeValue = R.path([GC.FIELD_TYPE], selectedReport);
    const filterPropsToUse = G.ifElse(G.isObject(filterProps), R.values(filterProps), filterProps);

    const entityParams = G.ifElse(
      R.and(G.isArray(filterPropsToUse), G.isNotNilAndNotEmpty(filterPropsToUse)),
      filterPropsToUse,
      settings,
    );

    const availableFields = G.setFilterByParams(
      G.getRefListByReportType(allAvailableRefTypesByScopeName, reportTypeValue),
      entityParams,
      { reportType: reportTypeValue },
    );

    const filterFields = R.pathOr([], ['searchCriteria'], selectedReport);

    const closePopper = () => {
      closeModal();
      setOpenPromptModal(false);
    };

    const component = (
      <OutsideReportExactFilterList
        {...propsRef.current}
        prompt={true}
        filterFieldWidth={150}
        useExactFilters={true}
        hideFilterHeader={true}
        closePopper={closePopper}
        useMenuPortalTarget={true}
        filterFields={filterFields}
        usedReport={selectedReport}
        availableFields={availableFields}
        setPromptStatus={setOpenPromptModal}
        handleChangeReportParams={handleChangeReportParams}
        handleSetUsedReport={() => handleSetUsedReport(setOpenPromptModal)}
      />
    );

    const modal = {
      p: 15,
      component,
      options: {
        title: G.getWindowLocale('actions:report-parameters', 'Report Parameters'),
      },
    };

    openModal(modal);
    setOpenPromptModal(false);
  }, [
    settings,
    openModal,
    closeModal,
    filterProps,
    selectedReport,
    handleSetUsedReport,
    handleChangeReportParams,
    allAvailableRefTypesByScopeName,
  ]);

  useEffect(() => {
    if (G.isNilOrEmpty(selectedReport)) return;

    if (G.isNotNilAndNotEmpty(reportType)) {
      const currentPath = G.getWindowLocationPathname();
      const selectedReportType = R.pathOr('', ['type'], selectedReport);

      if (G.notEquals(R.path([currentPath], reportTypesMap), selectedReportType)) return;
    }

    const selectedGuid = R.pathOr(null, [GC.FIELD_GUID], selectedReport);

    const guidChanged = G.isAllTrue(
      G.isNotNilAndNotEmpty(selectedGuid),
      G.notEquals(selectedGuid, prevSelectedReportGuid.current),
    );

    if (R.not(guidChanged)) return;

    const prompt = G.getPropFromObject(GC.FIELD_PROMPT, selectedReport);
    const defaultReport = R.pathOr(false, ['defaultReport'], selectedReport);
    const isDefaultReport = G.ifElse(G.isNilOrEmpty(pageVisited), G.isTrue(defaultReport), true);

    if (G.isAllTrue(G.isTrue(prompt), G.isTrue(isDefaultReport))) {
      handleOpenPromptModal();
      setOpenPromptModal(true);
    }

    prevSelectedReportGuid.current = selectedGuid;
  }, [reportType, pageVisited, selectedReport, handleOpenPromptModal]);

  return {
    openPromptModal,
    setOpenPromptModal,
    handleOpenPromptModal,
  };
};

export default usePromptModal;
